<template>
  <div class="view-area-box">
    <div class="view-area">
      <div class="view" :style="viewStyle">
        <svg class="path-svg" :width="containerWidth" :height="containerHeight">
          <template v-for="(item, index) in svgPathItems">
            <path v-if="!item.isHidden" :key="`path-${index}`"
              ref="path"
              class="point-path"
              :class="eventItemsCssClassData[item.index]"
              :d="item.d"/>
            <template v-if="item.scrollPaths">
              <path v-for="(scrollPath, scrollPathIndex) of item.scrollPaths"
                :key="`scroll-path-${item.index}-${scrollPathIndex}`"
                :class="{
                  ...eventItemsCssClassData[item.index],
                  'scroll-path': scrollPathIndex === 0,
                  'scroll-connect-path': scrollPathIndex !== 0
                }"
                :d="scrollPath"/>
            </template>
          </template>
        </svg>
        <template v-for="(item, index) of nodeItems">
          <div :key="index"
            class="node"
            :class="{
              ...eventItemsCssClassData[item.index],
              hide: isActionStart && item.index !== activeIndex,
              off: NODE_TYPE[item.type].isOff
            }"
            :style="{
              left: `${item.x}px`,
              top: `${item.y}px`
            }"
            @click="$emit('onNodeClick', item)">
            <span v-if="['scrollUp', 'scrollDown'].includes(item.type)"
              class="scroll-icon"></span>
            <span class="text">
              <template v-if="item.type === 'active'">A</template>
              <template v-else-if="item.type === 'inactive'">I</template>
              <template v-else>{{ item.nodeData.order }}</template>
            </span>
            <div v-if="isActionStart && activeItem && activeItem.index === item.index"
              class="item-tooltip"
              :class="{
                ...eventItemsCssClassData[item.index],
                top: activeItem.isTop
              }">
              <span class="name" :class="eventItemsCssClassData[item.index]">
                {{ NODE_TYPE[item.type].name }}
              </span>
              <span class="duration">
                {{ formatTime(activeItem.duration, 3, false) }}
              </span>
              <i v-if="!activeItem.isTop"
                class="arrow-up icon bg--box-arrow-up"></i>
              <i v-else
                class="arrow-down icon bg--box-arrow-down"></i>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurvedPathItems } from '@/libs/svg-helper'

export default {
  name: 'FunnelSessionDesktop',
  props: [
    'NODE_TYPE',
    'containerWidth',
    'containerHeight',
    'imageUrl',
    'activeIndex',
    'activeItem',
    'eventItems',
    'nodeItems',
    'eventItemsCssClassData',
    'formatTime',
    'isActionStart'
  ],
  computed: {
    viewStyle () {
      return {
        width: `${this.containerWidth}px`,
        height: `${this.containerHeight}px`,
        backgroundImage: `url(${this.imageUrl})`
      }
    },
    svgPathItems () {
      const pathPoints = this.eventItems.map(item => {
        return {
          x: item.x,
          y: item.y,
          index: item.index
        }
      })

      const items = getCurvedPathItems(pathPoints).map((item, index) => {
        const targetIndex = pathPoints[index].index
        const pathItem = this.eventItems.find(pathItem => pathItem.index === targetIndex)
        let scrollPaths = null

        if (pathItem.scrollData) {
          const width = 10
          let arrowHeight = 5
          const halfWidth = width / 2
          const x1 = pathItem.x
          const y1 = pathItem.y
          const dx = pathItem.scrollData.dx
          const dy = pathItem.scrollData.dy
          const isUp = dy < 0

          if (isUp) {
            arrowHeight = -arrowHeight
          }

          if (Math.abs(dy) >= 5) {
            scrollPaths = [
              [
                `M ${x1} ${y1}`,
                `l ${-halfWidth} 0`,
                `l 0 ${dy - arrowHeight}`,
                `l ${halfWidth} ${arrowHeight}`,
                `l ${halfWidth} ${-arrowHeight}`,
                `l 0 ${-dy + arrowHeight}`,
                'Z'
              ].join(' '),
              [
                `M ${x1} ${y1 + dy}`,
                `l ${dx} 0`
              ].join(' ')
            ]
          }
        }

        return {
          d: item.d,
          scrollPaths,
          isHidden: pathItem.isHidden,
          index: targetIndex
        }
      })
      return items
    }
  }
}
</script>

<style lang="scss" scoped>
.view-area-box {
  position: relative;
  height: 100%;
  box-sizing: border-box;
  padding: 40px 0;
  overflow-y: auto;
}

.item-tooltip {
  top: 80px;
  left: 50%;
  position: absolute;
  box-sizing: border-box;
  transform: translateX(-50%);
  padding: 15px;
  border-radius: 6px;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #ffffff;
  &.top {
    top: -85px;
  }

  &.node-active,
  &.node-inactive {
    top: 49px;
  }
  &.node-active.top,
  &.node-inactive.top {
    top: -85px;
  }

  &> span {
    white-space: nowrap;
    display: block;
    text-align: center;
    font-size: 13px;
    line-height: 13px;
    color: #767676;
    font-weight: bold;

    &.name {
      padding: 0 2px 6px 2px;
      margin-bottom: 6px;
      border-bottom: 1px solid #ebebeb;

      &.node-hover {
        color: #289eef;
      }
      &.node-click {
        color: #efae00;
      }
      &.node-hover-click {
        color: #ab85ff;
      }
    }
    &.duration {
      color: #444;
      padding-top: 4px;
    }
  }

  .arrow-up,
  .arrow-down {
    @include positionXcenter;
    z-index: 20;
    top: -8px;
  }
  .arrow-down {
    top: auto;
    bottom: -8px;
  }
}

.view {
  position: relative;
  z-index: 20;
  margin: 0 auto;
  background-repeat: no-repeat;
  background-size: 100%;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0.6;
    background-color: #252d3c;
  }

  .node {
    position: absolute;
    box-sizing: border-box;
    width: 48px;
    height: 48px;
    margin-left: -24px;
    margin-top: -24px;
    border-radius: 50%;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    background-color: #fff;
    transition: all 0.3s;
    cursor: pointer;

    &.active {
      width: 70px;
      height: 70px;
      margin-left: -35px;
      margin-top: -35px;
      border: 2px solid #fff;
      z-index: 10;
    }
    &:not(.active).hide,
    &:not(.active).off {
      visibility: hidden;
      opacity: 0;
      width: 0;
      height: 0;
      margin-left: 0;
      margin-top: 0;
    }

    .text {
      @include positionXYcenter;
      color: #fff;
      font-size: 14px;
      font-weight: bold;
    }

    &.node-active,
    &.node-inactive {
      width: 20px;
      height: 20px;
      margin-left: -10px;
      margin-top: -10px;
      border-radius: 4px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
      .text {
        font-size: 13px;
        font-weight: bold;
      }
      &:not(.active-initial).active {
        width: 40px;
        height: 40px;
        margin-left: -20px;
        margin-top: -20px;
      }
    }

    &.node-active {
      background-color: #fff;
      .text {
        color: rgba(0,0,0,.7);
      }
    }
    &.node-inactive {
      border: solid 1px #b1b1b1;
      background-color: #000000;
    }
    &.node-hover {
      background-color: #289eef;
    }
    &.node-click {
      background-color: #ffd738;
    }
    &.node-hover-click {
      background-color: #ab85ff;
    }
    &.node-click,
    &.node-scroll-up,
    &.node-scroll-down {
      .text {
        color: rgba(0, 0, 0, 0.75);
      }
    }
    &.node-scroll-up,
    &.node-scroll-down {
      .scroll-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 14px;
        height: 14px;
        margin-left: -7px;
        margin-top: -16px;
        background: url(../../../assets/img/funnel/icon_node_scroll_up.svg) no-repeat 0 0;
        background-size: cover;
      }
    }
    &.node-scroll-up {
      .text {
        margin-top: 5px;
      }
    }
    &.node-scroll-down {
      .scroll-icon {
        margin-top: 2px;
        transform: rotate(180deg);
      }
      .text {
        margin-top: -5px;
      }
    }
  }
}

.path-svg {
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;
}

.point-path {
  fill: transparent;
  stroke: #fff;
  stroke-width: 2;
  stroke-dasharray: 4 4;
  stroke-opacity: 0.7;
  &.active,
  &.active-prev,
  &.active-next {
    stroke-dasharray: 0;
    stroke-opacity: 1;
  }
}

.scroll-path {
  fill: #fff;
  fill-opacity: 0.2;

  &.active {
    fill-opacity: 0.8;
  }
  &.active-prev {
    fill-opacity: 0.5;
  }
  transition: all 0.3s;
}

.scroll-connect-path {
  fill: transparent;
  stroke: #fff;
  stroke-width: 2;
  stroke-dasharray: 4 4;
  stroke-opacity: 0.7;
  transition: all 0.3s;
}
</style>
